import React, { useState } from 'react';
import {
    Panel,
    PanelType,
    DetailsList,
    IColumn,
    SelectionMode,
    IconButton,
    Spinner,
    SpinnerSize,
    Shimmer,
} from '@fluentui/react';
import { useGetStorefrontCartQuery } from '../../../store/endpoints/StorefrontSettingsEndpoints';
import {
    useGetStorefrontBundleDetailQuery,
    useGetStorefrontProductDetailQuery,
} from '../../../store/endpoints/StorefrontProductEndpoints';
import {
    useGetProductBundleItemsQuery,
    useGetProductBundleQuery,
} from '../../../store/endpoints/ProductBundleEndpoints';
import { useGetCatalogsQuery, useGetItemDetailsQuery } from '../../../store/Api';
import StorefrontLabel from '../StorefrontLabel';
import DetailsModal from '../../DetailsModal/DetailsModal';
import { IStorefrontCartProduct, IStorefrontCartBundle, IStorefrontCart } from '../../../models/Storefront';
import { IStorefrontBundleDetailProduct } from '../../../models/StorefrontProducts';
import { IProductBundleItem } from '../../../models/ProductBundles';

const UNAVAILABLE = <span style={{ color: '#a19f9d', fontStyle: 'italic' }}>No longer available</span>;

// ─── Product row ──────────────────────────────────────────────────────────────
interface ICartProductRowProps {
    item: IStorefrontCartProduct;
    storefrontId: string;
}

const CartProductRow: React.FC<ICartProductRowProps> = ({ item, storefrontId }) => {
    const [detailsOpen, setDetailsOpen] = useState(false);

    // Primary: storefront API (pricing + catalog info)
    const { data: sfData, isFetching: sfFetching, isError: sfError } = useGetStorefrontProductDetailQuery(
        { storefrontId, productId: item.productId },
        { skip: !storefrontId }
    );

    // Fallback: when storefront API fails, try the default catalog
    const getCatalogs = useGetCatalogsQuery(undefined, { skip: !sfError });
    const defaultCatalog = getCatalogs.data?.[0]?.name;
    const { data: fallbackData, isError: fallbackError, isFetching: fallbackFetching } = useGetItemDetailsQuery(
        { catalog: defaultCatalog ?? '', id: item.productId },
        { skip: !sfError || !defaultCatalog }
    );

    const isLoading = sfFetching || (sfError && (getCatalogs.isFetching || fallbackFetching));
    const notFound = sfError && !isLoading && !!defaultCatalog && (fallbackError || !fallbackData);

    if (notFound) {
        return (
            <tr style={{ borderBottom: '1px solid #edebe9' }}>
                <td colSpan={5} style={{ padding: '6px 8px', color: '#a19f9d', fontStyle: 'italic' }}>
                    Product Not Found
                </td>
            </tr>
        );
    }

    const name = sfData?.name ?? fallbackData?.name ?? item.productId;
    const price = sfData?.basePrice;
    const cost = sfData?.cost?.cost;
    const catalog = sfData?.catalog ?? (fallbackData ? defaultCatalog : undefined);
    const priceUnavailable = sfError && !sfFetching;

    return (
        <tr style={{ borderBottom: '1px solid #edebe9', verticalAlign: 'middle' }}>
            <td style={{ padding: '6px 8px', minWidth: 180 }}>
                {isLoading ? <Shimmer width="120px" /> : <span>{name}</span>}
            </td>
            <td style={{ padding: '6px 8px', textAlign: 'right', minWidth: 80 }}>{item.quantity}</td>
            <td style={{ padding: '6px 8px', textAlign: 'right', minWidth: 90 }}>
                {isLoading
                    ? <Shimmer width="60px" />
                    : priceUnavailable
                        ? UNAVAILABLE
                        : price != null ? price.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : '—'}
            </td>
            <td style={{ padding: '6px 8px', textAlign: 'right', minWidth: 90 }}>
                {isLoading
                    ? <Shimmer width="60px" />
                    : priceUnavailable
                        ? '—'
                        : cost != null ? cost.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : '—'}
            </td>
            <td style={{ padding: '6px 8px' }}>
                {catalog && (
                    <>
                        <IconButton
                            iconProps={{ iconName: 'OpenPane' }}
                            title="Product Details"
                            onClick={() => setDetailsOpen(true)}
                        />
                        <DetailsModal
                            product={item.productId}
                            catalog={catalog}
                            open={detailsOpen}
                            onClose={() => setDetailsOpen(false)}
                            preventEdit
                        />
                    </>
                )}
            </td>
        </tr>
    );
};

// ─── Bundle row ───────────────────────────────────────────────────────────────
interface ICartBundleRowProps {
    item: IStorefrontCartBundle;
    storefrontId: string;
}

const bundleProductColumns: IColumn[] = [
    { key: 'name', name: 'Product', fieldName: 'name', minWidth: 140, isResizable: true },
    { key: 'quantity', name: 'Qty', fieldName: 'quantity', minWidth: 50, maxWidth: 60 },
    {
        key: 'price', name: 'Price', minWidth: 80, maxWidth: 100,
        onRender: (p: IStorefrontBundleDetailProduct) =>
            p.price != null ? p.price.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : '—'
    },
    {
        key: 'cost', name: 'Cost', minWidth: 80, maxWidth: 100,
        onRender: (p: IStorefrontBundleDetailProduct) =>
            p.cost?.cost != null ? p.cost.cost.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : '—'
    },
];

// Fallback columns when only the admin bundle API is available (no storefront pricing)
const bundleFallbackColumns: IColumn[] = [
    {
        key: 'name', name: 'Product', minWidth: 200, isResizable: true,
        onRender: (p: IProductBundleItem) => p.sourceItem?.name ?? p.productId,
    },
    {
        key: 'quantity', name: 'Qty', minWidth: 50, maxWidth: 60,
        onRender: (p: IProductBundleItem) => p.details?.quantity ?? '—',
    },
];

const CartBundleRow: React.FC<ICartBundleRowProps> = ({ item, storefrontId }) => {
    const [expanded, setExpanded] = useState(false);

    const { data, isFetching, isError } = useGetStorefrontBundleDetailQuery(
        { storefrontId, bundleId: item.bundleId },
        { skip: !storefrontId }
    );

    const unavailable = isError && !isFetching;

    // Fallback: load from admin bundle API when the storefront API 404s
    const fallbackBundle = useGetProductBundleQuery(item.bundleId, { skip: !unavailable });
    const fallbackItems = useGetProductBundleItemsQuery(item.bundleId, { skip: !unavailable });

    const name = data?.name ?? fallbackBundle.data?.name ?? item.name ?? '-';
    const price = data?.price;
    const cost = data?.cost;

    const hasExpandableItems = unavailable
        ? (fallbackItems.data?.items?.length ?? 0) > 0
        : (data?.products?.length ?? 0) > 0;

    if (unavailable && fallbackBundle.isError) {
        return (
            <tr style={{ borderBottom: '1px solid #edebe9' }}>
                <td colSpan={5} style={{ padding: '6px 8px', color: '#a19f9d', fontStyle: 'italic' }}>
                    Bundle Not Found
                </td>
            </tr>
        );
    }

    return (
        <>
            <tr style={{ borderBottom: '1px solid #edebe9', verticalAlign: 'middle' }}>
                <td style={{ padding: '6px 8px', minWidth: 180 }}>
                    {isFetching || fallbackBundle.isLoading ? <Shimmer width="120px" /> : <span>{name}</span>}
                </td>
                <td style={{ padding: '6px 8px', textAlign: 'right', minWidth: 80 }}>{item.quantity}</td>
                <td style={{ padding: '6px 8px', textAlign: 'right', minWidth: 90 }}>
                    {isFetching || fallbackBundle.isLoading
                        ? <Shimmer width="60px" />
                        : unavailable
                            ? UNAVAILABLE
                            : price != null ? price.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : '—'}
                </td>
                <td style={{ padding: '6px 8px', textAlign: 'right', minWidth: 90 }}>
                    {isFetching || fallbackBundle.isLoading
                        ? <Shimmer width="60px" />
                        : unavailable
                            ? '—'
                            : cost != null ? cost.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : '—'}
                </td>
                <td style={{ padding: '6px 8px' }}>
                    {hasExpandableItems && (
                        <IconButton
                            iconProps={{ iconName: expanded ? 'ChevronUp' : 'ChevronDown' }}
                            title={expanded ? 'Hide products' : 'Show products'}
                            onClick={() => setExpanded(e => !e)}
                        />
                    )}
                </td>
            </tr>
            {expanded && (
                <tr>
                    <td colSpan={5} style={{ paddingLeft: 24, paddingBottom: 8 }}>
                        {unavailable && fallbackItems.data?.items
                            ? <DetailsList
                                selectionMode={SelectionMode.none}
                                items={fallbackItems.data.items}
                                columns={bundleFallbackColumns}
                                compact
                              />
                            : data?.products &&
                              <DetailsList
                                selectionMode={SelectionMode.none}
                                items={data.products}
                                columns={bundleProductColumns}
                                compact
                              />
                        }
                    </td>
                </tr>
            )}
        </>
    );
};

// ─── Panel ────────────────────────────────────────────────────────────────────
interface IStorefrontCartPanelProps {
    cartId?: string;
    onDismiss: () => void;
}

const tableHeaderStyle: React.CSSProperties = {
    padding: '6px 8px',
    textAlign: 'left',
    fontWeight: 600,
    borderBottom: '2px solid #edebe9',
    color: '#323130',
};

const StorefrontCartPanel: React.FC<IStorefrontCartPanelProps> = ({ cartId, onDismiss }) => {
    const cart = useGetStorefrontCartQuery(cartId ?? '', { skip: !cartId });
    const [cartData, setCartData] = React.useState<IStorefrontCart | undefined>();
    const storefrontId = cart.data?.storefront ?? '';

    React.useEffect(() => {
        if (cart.data) {
            setCartData(cart.data);
        }
    }, [cart.data]);

    React.useEffect(() => {
        if (cartId !== cartData?.id) {
            setCartData(undefined);
        }
    }, [cartId, cartData?.id]);

    return (
        <Panel
            isOpen={!!cartId}
            onDismiss={onDismiss}
            type={PanelType.medium}
            headerText={cartData?.emailAddress ? `Cart — ${cartData.emailAddress}` : 'Loading...'}
        >
            {cartData == null && <Spinner size={SpinnerSize.medium} />}
            {cartData && (
                <>
                    <p>
                        <span style={{ fontWeight: 'bold' }}>Storefront: </span>
                        <StorefrontLabel storefrontId={cartData.storefront} />
                    </p>
                    <p>
                        <span style={{ fontWeight: 'bold' }}>Email: </span>
                        {cartData.emailAddress}
                    </p>
                    <p>
                        <span style={{ fontWeight: 'bold' }}>Created: </span>
                        {new Date(cartData.createDate).toLocaleString()}
                    </p>
                    <p>
                        <span style={{ fontWeight: 'bold' }}>Last Visit: </span>
                        {new Date(cartData.lastVisit).toLocaleString()}
                    </p>
                    <p>
                        <span style={{ fontWeight: 'bold' }}>Last Updated: </span>
                        {new Date(cartData.lastUpdate).toLocaleString()}
                    </p>

                    {cartData.products && cartData.products.length > 0 && (
                        <>
                            <h3 style={{ marginBottom: 4 }}>Products ({cartData.products.length})</h3>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                                <thead>
                                    <tr>
                                        <th style={tableHeaderStyle}>Name</th>
                                        <th style={{ ...tableHeaderStyle, textAlign: 'right' }}>Qty</th>
                                        <th style={{ ...tableHeaderStyle, textAlign: 'right' }}>Price</th>
                                        <th style={{ ...tableHeaderStyle, textAlign: 'right' }}>Cost</th>
                                        <th style={tableHeaderStyle}></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {cartData.products.map(p => (
                                        <CartProductRow
                                            key={p.productId}
                                            item={p}
                                            storefrontId={storefrontId}
                                        />
                                    ))}
                                </tbody>
                            </table>
                        </>
                    )}

                    {cartData.bundles && cartData.bundles.length > 0 && (
                        <>
                            <h3 style={{ marginTop: 20, marginBottom: 4 }}>Bundles ({cartData.bundles.length})</h3>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                                <thead>
                                    <tr>
                                        <th style={tableHeaderStyle}>Name</th>
                                        <th style={{ ...tableHeaderStyle, textAlign: 'right' }}>Qty</th>
                                        <th style={{ ...tableHeaderStyle, textAlign: 'right' }}>Price</th>
                                        <th style={{ ...tableHeaderStyle, textAlign: 'right' }}>Cost</th>
                                        <th style={tableHeaderStyle}></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {cartData.bundles.map(b => (
                                        <CartBundleRow
                                            key={b.bundleId}
                                            item={b}
                                            storefrontId={storefrontId}
                                        />
                                    ))}
                                </tbody>
                            </table>
                        </>
                    )}

                    {(!cartData.products || cartData.products.length === 0) &&
                     (!cartData.bundles || cartData.bundles.length === 0) && (
                        <p style={{ color: '#666' }}>This cart has no items.</p>
                    )}
                </>
            )}
        </Panel>
    );
};

export default StorefrontCartPanel;

